import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  MapPin,
  Home as HomeIcon,
  Clock,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Utensils,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEvent } from '../hooks/useEvents';
import { useRSVPs } from '../hooks/useRSVPs';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Input, Select } from '../components/ui/Input';
import { formatDisplayDate, formatTime } from '../lib/dates';
import { DishCategory, RSVPStatus } from '../types';

const DISH_CATEGORIES: DishCategory[] = [
  'Main', 'Side', 'Dessert', 'Drink', 'Bread', 'Other',
];

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { event, loading: eventLoading } = useEvent(id);
  const { rsvps, loading: rsvpLoading, upsertRSVP, myRSVP, comingCount } =
    useRSVPs(id);

  const [rsvpStatus, setRsvpStatus] = useState<RSVPStatus | ''>('');
  const [dishName, setDishName] = useState('');
  const [dishCategory, setDishCategory] = useState<DishCategory | ''>('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRsvpForm, setShowRsvpForm] = useState(false);

  const currentRSVP = user ? myRSVP(user.id) : null;

  const initForm = () => {
    if (currentRSVP) {
      setRsvpStatus(currentRSVP.status);
      setDishName(currentRSVP.dish_name ?? '');
      setDishCategory((currentRSVP.dish_category as DishCategory) ?? '');
    }
    setShowRsvpForm(true);
  };

  const handleSaveRSVP = async () => {
    if (!user || !rsvpStatus) return;
    setSaving(true);
    const { error } = await upsertRSVP(
      user.id,
      rsvpStatus as RSVPStatus,
      dishName || undefined,
      dishCategory as DishCategory || undefined
    );
    setSaving(false);
    if (!error) {
      setSaved(true);
      setShowRsvpForm(false);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const hostAddress = event?.location_override || event?.host?.address;
  const mapsUrl = hostAddress
    ? `https://maps.google.com/?q=${encodeURIComponent(hostAddress)}`
    : null;

  // Group dishes by category
  const dishesByCategory = DISH_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = rsvps.filter(
      (r) => r.status === 'coming' && r.dish_category === cat && r.dish_name
    );
    return acc;
  }, {} as Record<DishCategory, typeof rsvps>);

  const comingRSVPs = rsvps.filter((r) => r.status === 'coming');
  const notComingRSVPs = rsvps.filter((r) => r.status === 'not_coming');

  if (eventLoading) return <AppShell><LoadingSpinner /></AppShell>;
  if (!event) return (
    <AppShell>
      <PageHeader title="Event" back />
      <div className="px-4 pt-4 text-center text-gray-500">Event not found.</div>
    </AppShell>
  );

  return (
    <AppShell>
      <PageHeader title={event.title} subtitle={formatDisplayDate(event.date)} back />

      <div className="px-4 pt-3 space-y-4 pb-4">
        {/* Event Info */}
        <Card>
          <div className="space-y-3">
            {event.time && (
              <InfoRow icon={<Clock size={15} />} label={formatTime(event.time)} />
            )}

            {event.host ? (
              <InfoRow
                icon={<HomeIcon size={15} />}
                label={`Hosted by ${event.host.full_name.split(' ')[0]}${event.host.spouse_name ? ` & ${event.host.spouse_name}` : ''}`}
              />
            ) : (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-950 border border-amber-100 dark:border-amber-900 px-3 py-2.5">
                <p className="text-amber-700 dark:text-amber-300 text-sm">No host assigned yet.</p>
              </div>
            )}

            {hostAddress && (
              <InfoRow
                icon={<MapPin size={15} />}
                label={
                  mapsUrl ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-warm-600 underline underline-offset-2"
                    >
                      {hostAddress}
                    </a>
                  ) : (
                    hostAddress
                  )
                }
              />
            )}

            {event.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed pt-1 border-t border-warm-50 dark:border-gray-800">
                {event.description}
              </p>
            )}
          </div>
        </Card>

        {/* My RSVP */}
        <Card>
          <CardHeader
            title="My RSVP"
            subtitle={
              currentRSVP
                ? currentRSVP.status === 'coming'
                  ? '✓ You\'re coming'
                  : '✗ You\'re not coming'
                : 'You haven\'t responded yet'
            }
          />

          {saved && (
            <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2 mb-3 text-sm text-green-700 dark:text-green-300">
              RSVP saved!
            </div>
          )}

          {!showRsvpForm ? (
            <Button variant="secondary" size="sm" onClick={initForm}>
              {currentRSVP ? 'Edit RSVP' : 'Respond'}
            </Button>
          ) : (
            <div className="space-y-3">
              {/* Coming / Not Coming */}
              <div className="flex gap-2">
                <button
                  onClick={() => setRsvpStatus('coming')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    rsvpStatus === 'coming'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Check size={15} /> Coming
                </button>
                <button
                  onClick={() => setRsvpStatus('not_coming')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    rsvpStatus === 'not_coming'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <X size={15} /> Not Coming
                </button>
              </div>

              {rsvpStatus === 'coming' && (
                <>
                  <Input
                    label="What dish are you bringing? (optional)"
                    value={dishName}
                    onChange={(e) => setDishName(e.target.value)}
                    placeholder="e.g. Macaroni salad"
                  />
                  <Select
                    label="Category"
                    value={dishCategory}
                    onChange={(e) =>
                      setDishCategory(e.target.value as DishCategory)
                    }
                  >
                    <option value="">Select category…</option>
                    {DISH_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                </>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRsvpForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  loading={saving}
                  disabled={!rsvpStatus}
                  onClick={handleSaveRSVP}
                >
                  Save RSVP
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Dishes */}
        <Card>
          <CardHeader
            title="Dishes"
            subtitle={`${comingCount} people coming`}
          />
          {rsvpLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <div className="space-y-3">
              {DISH_CATEGORIES.map((cat) => {
                const items = dishesByCategory[cat];
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Utensils size={12} className="text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {cat}
                      </span>
                    </div>
                    {items.length === 0 ? (
                      <p className="text-xs text-gray-300 dark:text-gray-600 ml-4 italic">
                        Still needed
                      </p>
                    ) : (
                      <div className="ml-4 space-y-1">
                        {items.map((r) => (
                          <div key={r.id} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {r.dish_name}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {r.profile?.full_name?.split(' ')[0]}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Attendees */}
        <Card>
          <AttendeeSection
            coming={comingRSVPs}
            notComing={notComingRSVPs}
            loading={rsvpLoading}
          />
        </Card>
      </div>
    </AppShell>
  );
}

function InfoRow({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 bg-warm-50 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 text-warm-500">
        {icon}
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 leading-snug">{label}</div>
    </div>
  );
}

function AttendeeSection({
  coming,
  notComing,
  loading,
}: {
  coming: any[];
  notComing: any[];
  loading: boolean;
}) {
  const [showNotComing, setShowNotComing] = useState(false);

  return (
    <>
      <CardHeader title="Attendees" />
      {loading ? (
        <LoadingSpinner size="sm" />
      ) : (
        <>
          {coming.length === 0 && notComing.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">No responses yet.</p>
          ) : (
            <>
              {coming.length > 0 && (
                <div className="space-y-2 mb-3">
                  {coming.map((r) => (
                    <div key={r.id} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {r.profile?.full_name ?? 'Member'}
                      </span>
                      <Badge variant="green">Coming</Badge>
                    </div>
                  ))}
                </div>
              )}

              {notComing.length > 0 && (
                <>
                  <button
                    onClick={() => setShowNotComing((s) => !s)}
                    className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 mt-1"
                  >
                    {showNotComing ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {notComing.length} not coming
                  </button>
                  {showNotComing && (
                    <div className="space-y-2 mt-2">
                      {notComing.map((r) => (
                        <div key={r.id} className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            {r.profile?.full_name ?? 'Member'}
                          </span>
                          <Badge variant="red">Not Coming</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
